import React, {useCallback} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import Modal, {useModal} from '@stencil/hotels-modal-component';
import Button from '../../../../core-components/Button/Button';

//GA Events
import {pushga} from 'ga-utils';
import {gaConfig} from '@stencil/hotels-config';
const {DetailPageGAEvents} = gaConfig;

import {
  reviewDataSelector,
  selRnrTagIds,
  reviewDetailTypeSelector,
  rnrPaginatorStatus,
} from '@stencil/hotels-ugc-redux/src/hotels-ugc-selectors';
import {
  updatePaginatorStatus,
  userReviewDetailType,
  updateRnrTags,
} from '@stencil/hotels-ugc-redux/src/hotels-ugc-actions';
import {DEFAULT_RNR_OFFSET, DEFAULT_RNR_LIMIT} from '@stencil/hotels-ugc-redux/src/ugc-config';

import {
  WrapperDiv,
  ContentWrapperDiv,
  HeadingWrapper,
  HeadingTextStyle,
  MainContentWrapperDiv,
} from './WhatToKnow.styles';

import * as S from './WhatToKnow.styles';

const WhatToKnow = () => {
  const reviewData = useSelector(reviewDataSelector, shallowEqual);
  const selTagIds = useSelector(selRnrTagIds, shallowEqual) || [];
  const reviewDetailType = useSelector(reviewDetailTypeSelector, shallowEqual);
  const paginatorStatus = useSelector(rnrPaginatorStatus, shallowEqual);
  const {filter, sortBy} = reviewDetailType;

  // For Modal
  const {isModalOpen, closeModal, openModal} = useModal();
  const {tagData = []} = reviewData;
  const shownTags = tagData.slice(0, 4);
  const hiddenTags = tagData.slice(4);
  const dispatch = useDispatch();

  const updatePaginationStatus = useCallback(
    status => {
      return dispatch(updatePaginatorStatus(status));
    },
    [paginatorStatus],
  );

  const updateSelTags = useCallback(
    data => {
      return dispatch(updateRnrTags(data));
    },
    [selTagIds],
  );

  const updateReviewDetail = useCallback(
    detailType => {
      return dispatch(userReviewDetailType(detailType));
    },
    [reviewDetailType],
  );

  const getStyleForTag = sentiment => {
    if (sentiment === 'POS') {
      return 'greenWrap';
    } else if (sentiment === 'NEU') {
      return 'yellowWrap';
    } else if (sentiment === 'NEG') {
      return 'redWrap';
    } else {
      return 'yellowWrap';
    }
  };

  const onTagSelection = id => {
    if (selTagIds.includes(id)) {
      updateSelTags(selTagIds.filter(item => item !== id));
      pushga({
        eventCategory: DetailPageGAEvents.reviewConsumptionCategory,
        eventAction: DetailPageGAEvents.tagUnclicked,
        eventLabel: name,
      });
    } else {
      updateSelTags(selTagIds.concat(id));
      pushga({
        eventCategory: DetailPageGAEvents.reviewConsumptionCategory,
        eventAction: DetailPageGAEvents.tagClicked,
        eventLabel: name,
      });
    }
  };

  const showFinalReviews = () => {
    updatePaginationStatus(paginatorStatus.concat(`tag_${selTagIds}`));
    updateReviewDetail({
      offset: DEFAULT_RNR_OFFSET,
      limit: DEFAULT_RNR_LIMIT,
      tagIds: selTagIds,
      filter: filter,
      sortBy: sortBy,
    });
    closeModal();
    pushga({
      eventCategory: DetailPageGAEvents.reviewConsumptionCategory,
      eventAction: DetailPageGAEvents.hideTags,
    });
  };

  const onSelectTag = (id, name) => {
    if (selTagIds.includes(id)) {
      updatePaginationStatus(paginatorStatus.filter(item => item !== `tag_${id}`));
      updateSelTags(selTagIds.filter(item => item !== id));
      updateReviewDetail({
        offset: DEFAULT_RNR_OFFSET,
        limit: DEFAULT_RNR_LIMIT,
        tagIds: selTagIds.filter(item => item !== id),
        filter: filter,
        sortBy: sortBy,
      });
      pushga({
        eventCategory: DetailPageGAEvents.reviewConsumptionCategory,
        eventAction: DetailPageGAEvents.tagUnclicked,
        eventLabel: name,
      });
    } else {
      updatePaginationStatus(paginatorStatus.concat(`tag_${id}`));
      updateSelTags(selTagIds.concat(id));
      updateReviewDetail({
        offset: DEFAULT_RNR_OFFSET,
        limit: DEFAULT_RNR_LIMIT,
        tagIds: selTagIds.concat(id),
        filter: filter,
        sortBy: sortBy,
      });
      pushga({
        eventCategory: DetailPageGAEvents.reviewConsumptionCategory,
        eventAction: DetailPageGAEvents.tagClicked,
        eventLabel: name,
      });
    }
    return;
  };

  return (
    <WrapperDiv>
      <ContentWrapperDiv>
        <HeadingWrapper>
          <HeadingTextStyle>What our guests say?</HeadingTextStyle>
        </HeadingWrapper>
      </ContentWrapperDiv>
      <MainContentWrapperDiv>
        <S.OptionsWrapperDiv>
          {shownTags.map(item => (
            <li
              key={item.name}
              className={
                selTagIds.includes(item.id)
                  ? `${getStyleForTag(item.sentiment)} active`
                  : `${getStyleForTag(item.sentiment)}`
              }
              onClick={() => onSelectTag(item.id, item.name)}
            >
              <S.OptionsKindWrapSpan>
                {item.name} ({item.count})
              </S.OptionsKindWrapSpan>
            </li>
          ))}
          {hiddenTags.length > 0 && (
            <li
              className="greenWrap"
              onClick={() => {
                openModal(),
                  pushga({
                    eventCategory: DetailPageGAEvents.reviewConsumptionCategory,
                    eventAction: DetailPageGAEvents.seeAllTags,
                  });
              }}
            >
              <S.OptionsKindWrapSpan>{`+ ${hiddenTags.length} more`}</S.OptionsKindWrapSpan>
            </li>
          )}
        </S.OptionsWrapperDiv>

        <Modal
          isOpen={isModalOpen}
          closeModal={() => {
            closeModal(),
              pushga({
                eventCategory: DetailPageGAEvents.reviewConsumptionCategory,
                eventAction: DetailPageGAEvents.hideTags,
              });
          }}
          modalStyles={{
            height: 'auto',
            width: 'auto',
          }}
          CloseButton={() => null}
        >
          <S.TripPopWrapperDiv>
            <S.TripAdvisorContentWrapperDiv>
              <S.TripAdvisorHeaderDiv>
                <S.HeaderTxtDiv>What our guests say?</S.HeaderTxtDiv>
              </S.TripAdvisorHeaderDiv>
              <S.TripAdvisorPopMainContentDiv>
                <S.OptionsWrapperDiv>
                  {tagData.map(item => (
                    <li
                      key={item.name}
                      className={
                        selTagIds && selTagIds.includes(item.id)
                          ? `${getStyleForTag(item.sentiment)} active`
                          : `${getStyleForTag(item.sentiment)}`
                      }
                      onClick={() => onTagSelection(item.id)}
                    >
                      <S.OptionsKindWrapSpan>
                        {item.name} ({item.count})
                      </S.OptionsKindWrapSpan>
                    </li>
                  ))}
                </S.OptionsWrapperDiv>
              </S.TripAdvisorPopMainContentDiv>

              <S.LastContentDiv>
                <Button onClick={() => showFinalReviews()}>
                  <S.ShowReviewSpan>SHOW REVIEWS</S.ShowReviewSpan>
                </Button>
              </S.LastContentDiv>
            </S.TripAdvisorContentWrapperDiv>
          </S.TripPopWrapperDiv>
        </Modal>
      </MainContentWrapperDiv>
    </WrapperDiv>
  );
};

export default WhatToKnow;
